/**
 * Envelope estándar de respuestas exitosas del backend NestJS (sisma-nest).
 * Definido por el `ResponseInterceptor` global del backend.
 */
export interface ApiResponse<T> {
  success: true;
  data: T;
  timestamp: string;
}

/**
 * Forma JSON del error que devuelve el `AllExceptionsFilter` del backend.
 * `message` puede ser un string o un array (cuando el ValidationPipe lista varios errores).
 * Es la *forma del payload*, no la excepción que se lanza en el frontend.
 */
export interface ApiErrorPayload {
  statusCode: number;
  timestamp: string;
  path: string;
  message: string | readonly string[];
}

/**
 * Excepción tipada que se lanza/rechaza desde el cliente HTTP.
 * Extiende `Error` para que stacks, `instanceof` y reglas de lint funcionen,
 * y conserva todos los metadatos del payload original.
 */
export class ApiError extends Error implements ApiErrorPayload {
  readonly statusCode: number;
  readonly timestamp: string;
  readonly path: string;
  readonly rawMessage: string | readonly string[];

  constructor(payload: ApiErrorPayload) {
    const flatMessage =
      typeof payload.message === 'string' ? payload.message : payload.message.join(', ');
    super(flatMessage);
    this.name = 'ApiError';
    this.statusCode = payload.statusCode;
    this.timestamp = payload.timestamp;
    this.path = payload.path;
    this.rawMessage = payload.message;
  }
}

export function isApiErrorPayload(value: unknown): value is ApiErrorPayload {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['statusCode'] === 'number' &&
    typeof v['path'] === 'string' &&
    typeof v['timestamp'] === 'string' &&
    (typeof v['message'] === 'string' || Array.isArray(v['message']))
  );
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
